import { bands as dashboardRecommendationBands } from "../components/dashboard/components/FilterModal/constants";

/**
 * @param {object} band
 * @returns {boolean}
 */
export function bandIsStatic(band) {
    if (!band || typeof band !== "object") return false;
    return band.is_static_band === true;
}

/**
 * @param {object} dynamicFields — recommendationsReducer.dynamicFields
 * @returns {Array<object>}
 */
export function getRecommendationGroupFieldsFromDynamicFields(dynamicFields) {
    if (!dynamicFields || typeof dynamicFields !== "object") return [];
    const raw = dynamicFields.recommendation_group_fields;
    return Array.isArray(raw) ? raw : [];
}

/**
 * Keys for bands marked static on `list_group_fields` / `recommendation_group_fields`.
 * @param {object} dynamicFields
 * @returns {Set<string>}
 */
function collectStaticBandKeysFromGroupFields(dynamicFields) {
    const keys = new Set();
    getRecommendationGroupFieldsFromDynamicFields(dynamicFields)
        .filter(bandIsStatic)
        .forEach(b => {
            if (b.name) keys.add(String(b.name));
        });
    return keys;
}

/**
 * Keys for bands where any field has `is_static_band` from `list_specific_fields`
 * (denormalized per field).
 * @param {object} dynamicFields
 * @returns {Set<string>}
 */
function collectStaticBandKeysFromCustomFields(dynamicFields) {
    const keys = new Set();
    const rcf = dynamicFields?.recommendation_custom_fields;
    if (!rcf || typeof rcf !== "object") return keys;
    for (const [topKey, fields] of Object.entries(rcf)) {
        const arr = Array.isArray(fields) ? fields : [];
        if (!arr.some(f => f?.is_static_band === true)) continue;
        keys.add(String(topKey));
        for (const f of arr) {
            if (f?.group_field != null && String(f.group_field)) keys.add(String(f.group_field));
        }
    }
    return keys;
}

/**
 * @param {object} dynamicFields
 * @returns {Set<string>}
 */
export function getStaticRecommendationBandKeySet(dynamicFields) {
    const keys = collectStaticBandKeysFromGroupFields(dynamicFields);
    collectStaticBandKeysFromCustomFields(dynamicFields).forEach(k => keys.add(k));
    return keys;
}

/**
 * @param {object} dynamicFields
 * @returns {string[]}
 */
export function getStaticRecommendationBandKeys(dynamicFields) {
    return [...getStaticRecommendationBandKeySet(dynamicFields)];
}

/**
 * All object keys that should be excluded from the generic DynamicBand accordion (id + name + slug variants).
 * @param {object} dynamicFields
 * @returns {string[]}
 */
export function getStaticRecommendationBandExcludeKeys(dynamicFields) {
    return getStaticRecommendationBandKeys(dynamicFields);
}

/**
 * Sort non-static band entries [key, fields][] by band order from metadata.
 * @param {Array<[string, unknown[]]>} bandEntries
 * @param {object} dynamicFields
 * @returns {Array<[string, unknown[]]>}
 */
export function sortDynamicRecommendationBandEntries(bandEntries, dynamicFields) {
    const merged = getRecommendationGroupFieldsFromDynamicFields(dynamicFields);
    const staticSet = getStaticRecommendationBandKeySet(dynamicFields);
    const orderOf = key => {
        const k = String(key);
        const found = merged.find(b => String(b.name) === k);
        if (found && found.order != null && !Number.isNaN(Number(found.order))) return Number(found.order);
        return 9999;
    };
    return [...bandEntries]
        .filter(([k]) => !staticSet.has(String(k)))
        .sort((a, b) => orderOf(a[0]) - orderOf(b[0]) || String(a[0]).localeCompare(String(b[0])));
}

/**
 * Map recommendation_custom_fields object key to dashboard filter tab band label.
 * @param {string} bandKey
 * @returns {string}
 */
export function recommendationCustomFieldBandKeyToFilterLabel(bandKey) {
    const k = String(bandKey);
    if (dashboardRecommendationBands[k]) return dashboardRecommendationBands[k];
    return k;
}
